/**
 * HistogramChart – a single-metric bar chart for the External-Load histogram view.
 *
 * Props:
 *   title         – metric label (e.g. "Total Distance")
 *   unit          – display unit (e.g. "km")
 *   color         – hex color for bars (e.g. "#6366f1")
 *   icon          – Lucide icon component (optional)
 *   players       – sorted array of { id, first_name, last_name, profile_picture, value }
 *   highlightId   – player id to highlight (others greyed-out), null = none
 *   onBarClick    – (playerId) => void
 *   isDarkMode    – boolean
 *   formatValue   – (v) => string   (optional, default rounds to 2 dec)
 *   isLoading     – boolean, true while new data is being fetched (bars keep old positions)
 */
import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";

const DEFAULT_FORMAT = (v) => (v != null ? Number(v).toFixed(2) : "–");

const CHART_HEIGHT = 180;

const BAR_TRANSITION = "height 650ms cubic-bezier(.4,0,.2,1), opacity 400ms ease, background 300ms ease, box-shadow 300ms ease, filter 200ms ease";

/* ── Trainability color helpers (mirrored from TeamOverview) ── */
const TRAIN_LOW_10 = 4;
const TRAIN_HIGH_10 = 6;

const clamp01 = (x) => Math.max(0, Math.min(1, x));

const hexToRgb = (hex) => {
  const n = parseInt(hex.replace("#", ""), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};

const rgbToHex = ({ r, g, b }) =>
  "#" + [r, g, b].map((c) => c.toString(16).padStart(2, "0")).join("");

const lerpColor = (aHex, bHex, t) => {
  const a = hexToRgb(aHex);
  const b = hexToRgb(bHex);
  const mix = (x, y) => Math.round(x + (y - x) * clamp01(t));
  return rgbToHex({ r: mix(a.r, b.r), g: mix(a.g, b.g), b: mix(a.b, b.b) });
};

const toScore10 = (raw) => {
  if (raw == null || !Number.isFinite(Number(raw))) return null;
  const v = Number(raw);
  return v >= 0 && v <= 1 ? v * 10 : v;
};

const trainabilityColor = (rawIndex, levelObj) => {
  const score = toScore10(rawIndex);
  if (score == null) return "#6b7280"; // gray-500 fallback
  const status = levelObj?.global ?? null;
    // 3-color logic: Red (<4), Yellow (4-6), Green (>6)
    if (score < 4) return "#DC2626"; // Red
    if (score <= 6) return "#F59E0B"; // Yellow
    return "#16A34A"; // Green
};

const formatTrainScore = (raw) => {
  const s = toScore10(raw);
  return s != null ? s.toFixed(1) : "–";
};

export default function HistogramChart({
  title,
  unit = "",
  color = "#6366f1",
  icon: Icon,
  players = [],
  highlightId = null,
  onBarClick,
  isDarkMode = false,
  formatValue = DEFAULT_FORMAT,
  isLoading = false,
}) {
  const [hoveredId, setHoveredId] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const chartRef = useRef(null);

  const stableRef = useRef(players);
  useEffect(() => {
    if (!isLoading && players.length > 0) stableRef.current = players;
  }, [players, isLoading]);
  const displayPlayers = isLoading && stableRef.current.length > 0 ? stableRef.current : players;

  const { scaleMax } = useMemo(() => {
    const vals = displayPlayers.map((p) => p.value).filter((v) => v != null && Number.isFinite(v));
    if (!vals.length) return { scaleMax: 1 };
    return { scaleMax: Math.max(...vals) * 1.12 };
  }, [displayPlayers]);

  const barHeight = useCallback(
    (val) => {
      if (val == null || !Number.isFinite(val) || scaleMax === 0) return 0;
      return Math.max(4, (val / scaleMax) * CHART_HEIGHT);
    },
    [scaleMax]
  );

  if (displayPlayers.length === 0) return null;

  // Accent neon layers (for title decorations)
  const neonLine  = `0 0 4px ${color}60, 0 0 10px ${color}35`;

  // Find hovered player for tooltip
  const hoveredPlayer = hoveredId != null ? displayPlayers.find((p) => p.id === hoveredId) : null;

  return (
    <div className="relative" ref={chartRef}>
      {/* ── Section title ── */}
      <div className="flex items-center gap-2 mb-2">
        {Icon && (
          <div
            className="flex items-center justify-center h-6 w-6 rounded-md"
            style={{
              background: `linear-gradient(135deg, ${color}30, ${color}15)`,
              boxShadow: neonLine,
            }}
          >
            <Icon className="h-3.5 w-3.5" style={{ color }} />
          </div>
        )}
        <h3
          className={`text-sm font-semibold tracking-wide ${
            isDarkMode ? "text-gray-200" : "text-gray-700"
          }`}
        >
          {title}
        </h3>
        {unit && (
          <span
            className={`text-[11px] font-medium ${
              isDarkMode ? "text-gray-500" : "text-gray-400"
            }`}
          >
            ({unit})
          </span>
        )}
        {/* Decorative line */}
        <div
          className="flex-1 h-px ml-2"
          style={{
            background: `linear-gradient(to right, ${color}50, transparent)`,
            boxShadow: neonLine,
          }}
        />
        {/* Subtle loading pulse */}
        {isLoading && (
          <div
            className="h-1 w-10 rounded-full animate-pulse"
            style={{ background: `${color}60` }}
          />
        )}
      </div>

      {/* ── Chart area – no background frame, seamless ── */}
      <div className="px-1 pt-3 pb-1" style={{ minHeight: CHART_HEIGHT + 70 }}>
        <div
          className="flex items-end w-full"
          style={{ height: CHART_HEIGHT + 50, gap: "2px" }}
        >
          {displayPlayers.map((p, idx) => {
            const isHovered = hoveredId === p.id;
            const dimmed = highlightId != null && p.id !== highlightId;
            const h = barHeight(p.value);
            const initials = (p.first_name?.[0] ?? "") + (p.last_name?.[0] ?? "");

            const barColor = trainabilityColor(p.trainability_index, p.trainability_level);
            const barNeonSoft = `0 0 6px ${barColor}55, 0 0 14px ${barColor}30`;
            const barNeonHover = `0 0 8px ${barColor}90, 0 0 20px ${barColor}60, 0 0 40px ${barColor}30`;

            return (
              <div
                key={p.id}
                className="flex-1 flex flex-col items-center cursor-pointer select-none min-w-0"
                style={{
                  opacity: dimmed ? 0.25 : 1,
                  transition: "opacity 300ms ease",
                  animationDelay: `${idx * 30}ms`,
                }}
                onClick={() => onBarClick?.(p.id)}
                onMouseEnter={(e) => {
                  setHoveredId(p.id);
                  if (chartRef.current) {
                    const rect = chartRef.current.getBoundingClientRect();
                    const barRect = e.currentTarget.getBoundingClientRect();
                    setTooltipPos({
                      x: barRect.left + barRect.width / 2 - rect.left,
                      y: barRect.top - rect.top - 8,
                    });
                  }
                }}
                onMouseLeave={() => setHoveredId(null)}
              >
                {/* Value label */}
                <span
                  className={`text-[9px] font-bold mb-1 whitespace-nowrap ${
                    isHovered
                      ? "opacity-100 scale-110"
                      : dimmed
                      ? "opacity-0"
                      : "opacity-70"
                  } ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                  style={{
                    transition: "opacity 200ms, transform 200ms, color 200ms",
                    ...(isHovered ? { color: barColor, textShadow: `0 0 8px ${barColor}80` } : {}),
                  }}
                >
                  {formatValue(p.value)}
                </span>

                {/* Bar – color from trainability gradient */}
                <div
                  className="w-full max-w-[28px] rounded-t-md relative"
                  style={{
                    height: h,
                    transition: BAR_TRANSITION,
                    background: dimmed
                      ? isDarkMode
                        ? "rgba(75,85,99,0.4)"
                        : "rgba(209,213,219,0.5)"
                      : `linear-gradient(to top, ${barColor}cc, ${barColor})`,
                    boxShadow: isHovered
                      ? barNeonHover
                      : dimmed
                      ? "none"
                      : barNeonSoft,
                    filter: isHovered ? "brightness(1.15)" : "none",
                  }}
                >
                  {!dimmed && (
                    <div
                      className="absolute inset-x-0 top-0 h-[2px] rounded-t-md"
                      style={{
                        background: `linear-gradient(to bottom, rgba(255,255,255,0.5), transparent)`,
                      }}
                    />
                  )}
                </div>

                {/* Avatar */}
                <div
                  className={`mt-1.5 rounded-full overflow-hidden flex items-center justify-center text-[8px] font-bold text-white flex-shrink-0 ${
                    dimmed ? "grayscale opacity-50" : ""
                  }`}
                  style={{
                    width: 22,
                    height: 22,
                    transition: "box-shadow 300ms ease, opacity 300ms ease, filter 300ms ease",
                    background: p.profile_picture
                      ? "transparent"
                      : `linear-gradient(135deg, ${barColor}cc, ${barColor}88)`,
                    boxShadow: isHovered ? `0 0 8px ${barColor}50` : "none",
                  }}
                >
                  {p.profile_picture ? (
                    <img
                      src={`data:image/jpeg;base64,${p.profile_picture}`}
                      alt={`${p.first_name} ${p.last_name}`}
                      className={`h-full w-full object-cover ${dimmed ? "grayscale" : ""}`}
                    />
                  ) : (
                    initials
                  )}
                </div>

                {/* Name */}
                <span
                  className={`mt-0.5 text-[8px] leading-tight text-center truncate w-full ${
                    isDarkMode ? "text-gray-500" : "text-gray-400"
                  } ${isHovered ? "font-semibold" : ""}`}
                  style={{
                    transition: "color 200ms ease",
                    ...(isHovered ? { color: barColor, textShadow: `0 0 6px ${barColor}50` } : {}),
                  }}
                >
                  {p.last_name?.slice(0, 6) ?? ""}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Hover tooltip ── */}
      {hoveredPlayer && (
        <div
          className="absolute pointer-events-none z-30"
          style={{
            left: tooltipPos.x,
            top: tooltipPos.y,
            transform: "translate(-50%, -100%)",
          }}
        >
          <div
            className={`px-3 py-2 rounded-lg shadow-lg text-xs whitespace-nowrap ${
              isDarkMode
                ? "bg-gray-800 border border-gray-700 text-gray-100"
                : "bg-white border border-gray-200 text-gray-800 shadow-md"
            }`}
          >
            <div className="font-semibold text-[11px] mb-1">
              {hoveredPlayer.first_name} {hoveredPlayer.last_name}
            </div>
            <div className="flex items-center gap-2">
              <span className={isDarkMode ? "text-gray-400" : "text-gray-500"}>{title}:</span>
              <span className="font-bold">{formatValue(hoveredPlayer.value)}</span>
              {unit && <span className={isDarkMode ? "text-gray-500" : "text-gray-400"}>{unit}</span>}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={isDarkMode ? "text-gray-400" : "text-gray-500"}>Trainability:</span>
              <span
                className="font-bold"
                style={{ color: trainabilityColor(hoveredPlayer.trainability_index, hoveredPlayer.trainability_level) }}
              >
                {formatTrainScore(hoveredPlayer.trainability_index)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
